/**
 * Stock Summary — inventory dashboard showing balance per item × warehouse.
 *
 * Design mirrors the invoice list (S11):
 *   • 4-tile KPI strip using Card + CardBody with left accent bar.
 *   • Tabs: All | Low stock | Zero stock.
 *   • Toolbar: search + warehouse filter.
 *   • table-fixed with <colgroup> widths, item avatar + name cell.
 *
 * @see apps/web/src/features/invoices/list/kpi-strip.tsx — KPI tile pattern
 * @see apps/web/src/features/invoices/list/invoice-table.tsx — table pattern
 * @see packages/api-contract/src/procedures/stock.ts — summaryInput / summaryRow
 */
import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  AlertTriangle,
  Boxes,
  IndianRupee,
  ListFilter,
  Plus,
  Search,
  Warehouse,
} from "lucide-react";
import type { stock as stockContract } from "@flint/api-contract";
import type { z } from "zod";
import {
  Card,
  CardBody,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  TabsList,
  TabsRoot,
  TabsTrigger,
} from "@flint/ui";
import { ListPagination } from "../../master-list/index.ts";
import { avatarClassFor, initialsFor } from "../../master-list/index.ts";
import { trpc } from "../../../lib/trpc.ts";
import { useModule } from "../../../lib/modules.ts";
import { paiseToInrShort, paiseToInr } from "../../../lib/format.ts";

type SummaryRow = z.infer<typeof stockContract.summaryRow>;
type TabId = "all" | "low_stock" | "zero_stock";

const TABS: Array<{ id: TabId; label: string }> = [
  { id: "all", label: "All" },
  { id: "low_stock", label: "Low stock" },
  { id: "zero_stock", label: "Zero stock" },
];

const PAGE_SIZE = 50;

export function StockSummaryPage() {
  // Stock transfers are the 1.0-withheld stock-ops module (ADR-0046) — hide the
  // entry so it doesn't dead-end at the route guard's redirect.
  const hasStockOps = useModule("stock-ops");
  const [activeTab, setActiveTab] = useState<TabId>("all");
  const [warehouseFilter, setWarehouseFilter] = useState<string | undefined>(undefined);
  const [draftQuery, setDraftQuery] = useState("");
  const [committedQuery, setCommittedQuery] = useState("");
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const cursor = cursorStack[cursorStack.length - 1];

  useEffect(() => {
    setCursorStack([]);
  }, [activeTab, warehouseFilter, committedQuery]);

  const summary = trpc.stock.summary.useQuery(
    {
      limit: PAGE_SIZE,
      tab: activeTab,
      ...(warehouseFilter ? { warehouseId: warehouseFilter } : {}),
      ...(committedQuery ? { q: committedQuery } : {}),
      ...(cursor !== undefined ? { cursor } : {}),
    },
    { placeholderData: (prev) => prev },
  );

  const warehouseList = trpc.warehouses.list.useQuery({ includeInactive: false });
  const rows = (summary.data?.items as SummaryRow[] | undefined) ?? [];

  const trackedCount = rows.filter((r) => r.quantityOnHand > 0).length;
  const totalValue = rows.reduce((sum, r) => sum + BigInt(r.totalValuePaise), 0n);
  const warehouseIds = new Set(rows.map((r) => r.warehouseId));
  const lowStockCount = rows.filter((r) => r.isBelowReorder).length;

  const pageNumber = cursorStack.length + 1;
  const showingFrom = rows.length === 0 ? 0 : cursorStack.length * PAGE_SIZE + 1;
  const showingTo = cursorStack.length * PAGE_SIZE + rows.length;
  const handlePrev = () => setCursorStack((s) => s.slice(0, -1));
  const handleNext = () => {
    const next = summary.data?.nextCursor;
    if (next) setCursorStack((s) => [...s, next]);
  };

  const submitSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCommittedQuery(draftQuery.trim());
  };

  return (
    <div className="px-6 pt-5 pb-16 max-w-[1320px] mx-auto w-full space-y-5">
      {/* ── Page head ─────────────────────────────────────────────── */}
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-ink tracking-tight leading-tight">
            Stock summary
          </h1>
          <p className="mt-1 text-sm text-muted">
            Current inventory levels across all warehouses
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0 flex-wrap">
          <Link
            to="/inventory/stock/opening"
            className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md border border-line bg-paper text-xs font-semibold text-ink-2 hover:bg-bg"
          >
            <Plus size={13} aria-hidden />
            Opening stock
          </Link>
          {hasStockOps ? (
            <Link
              to="/inventory/stock-transfers/new"
              className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md border border-line bg-paper text-xs font-semibold text-ink-2 hover:bg-bg"
            >
              Transfer
            </Link>
          ) : null}
          <Link
            to="/inventory/stock-adjustments/new"
            className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-md bg-ink text-paper text-sm font-semibold hover:bg-ink-2"
          >
            <Plus size={13} aria-hidden />
            New adjustment
          </Link>
        </div>
      </header>

      {/* ── KPI strip — matches invoice list Tile pattern ─────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        <KpiTile
          accent="sky"
          icon={<Boxes size={14} strokeWidth={2} />}
          label="Items tracked"
          value={String(trackedCount)}
          meta="with stock on hand"
        />
        <KpiTile
          accent="emerald"
          icon={<IndianRupee size={14} strokeWidth={2} />}
          label="Total value"
          value={paiseToInrShort(totalValue.toString())}
          meta="weighted average cost"
        />
        <KpiTile
          accent="sky"
          icon={<Warehouse size={14} strokeWidth={2} />}
          label="Warehouses"
          value={String(warehouseIds.size)}
          meta="with active stock"
        />
        <KpiTile
          accent={lowStockCount > 0 ? "amber" : "muted"}
          icon={<AlertTriangle size={14} strokeWidth={2} />}
          label="Low stock"
          value={String(lowStockCount)}
          meta={lowStockCount > 0 ? "below reorder level" : "all levels healthy"}
        />
      </div>

      {/* ── Tabs ──────────────────────────────────────────────────── */}
      <TabsRoot value={activeTab} onValueChange={(v) => setActiveTab(v as TabId)} className="mb-1">
        <TabsList aria-label="Stock filter">
          {TABS.map((t) => (
            <TabsTrigger key={t.id} value={t.id}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </TabsRoot>

      {/* ── Quick filters ─────────────────────────────────────────── */}
      <div className="flex items-center gap-2 flex-wrap pt-2">
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-muted shrink-0">
          <ListFilter size={13} aria-hidden />
          Quick filters
        </span>
        <QuickFilterPill
          label="Low stock"
          active={activeTab === "low_stock" && !warehouseFilter}
          onClick={() => { setActiveTab("low_stock"); setWarehouseFilter(undefined); }}
        />
        <QuickFilterPill
          label="Zero stock"
          active={activeTab === "zero_stock" && !warehouseFilter}
          onClick={() => { setActiveTab("zero_stock"); setWarehouseFilter(undefined); }}
        />
        {(warehouseList.data?.warehouses ?? []).map((w) => (
          <QuickFilterPill
            key={w.id}
            label={w.name}
            active={warehouseFilter === w.id}
            onClick={() => {
              setWarehouseFilter(warehouseFilter === w.id ? undefined : w.id);
              setActiveTab("all");
            }}
          />
        ))}
      </div>

      {/* ── Toolbar ───────────────────────────────────────────────── */}
      <div className="flex items-center gap-2 flex-wrap">
        <form
          onSubmit={submitSearch}
          className="flex flex-1 min-w-[200px] max-w-[340px] items-center gap-2 h-9 px-3 rounded-md border border-line bg-paper focus-within:border-sky-600 focus-within:ring-2 focus-within:ring-sky-100 transition-colors"
        >
          <Search size={14} aria-hidden className="text-muted shrink-0" />
          <input
            type="search"
            placeholder="Search by item name or SKU…"
            value={draftQuery}
            onChange={(e) => setDraftQuery(e.target.value)}
            className="flex-1 min-w-0 bg-transparent border-0 outline-0 text-sm placeholder:text-muted text-ink"
            aria-label="Search stock"
          />
        </form>

        <Select
          value={warehouseFilter ?? "all"}
          onValueChange={(v) => setWarehouseFilter(v === "all" ? undefined : v)}
        >
          <SelectTrigger className="h-9 w-[200px] text-xs">
            <Warehouse size={13} aria-hidden className="text-muted shrink-0" />
            <SelectValue placeholder="All warehouses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All warehouses</SelectItem>
            {(warehouseList.data?.warehouses ?? []).map((w) => (
              <SelectItem key={w.id} value={w.id}>
                {w.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* ── Table ─────────────────────────────────────────────────── */}
      {summary.isLoading ? (
        <p className="text-sm text-muted py-4">Loading stock…</p>
      ) : summary.error ? (
        <div className="rounded-md border border-line p-8 text-center bg-paper">
          <p className="text-sm font-semibold text-ink-2">Couldn't load stock</p>
          <p className="text-xs text-muted mt-1">{summary.error.message}</p>
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-md border border-line p-12 text-center bg-paper">
          <Boxes size={32} className="mx-auto text-muted/40 mb-3" aria-hidden />
          <h3 className="text-sm font-semibold text-ink-2">No stock entries yet</h3>
          <p className="text-xs text-muted mt-1 max-w-xs mx-auto">
            Enter your opening stock levels to get started, or record a stock adjustment.
          </p>
          <Link
            to="/inventory/stock/opening"
            className="mt-4 inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-ink text-paper text-xs font-semibold hover:bg-ink-2"
          >
            <Plus size={12} aria-hidden /> Enter opening stock
          </Link>
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border border-line bg-paper shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] table-fixed text-sm" aria-label="Stock summary">
              <colgroup>
                <col style={{ width: 220 }} />
                <col style={{ width: 116 }} />
                <col style={{ width: 120 }} />
                <col style={{ width: 152 }} />
                <col style={{ width: 72 }} />
                <col style={{ width: 60 }} />
                <col style={{ width: 104 }} />
                <col style={{ width: 100 }} />
              </colgroup>
              <thead className="bg-raised text-xs font-medium text-muted border-b border-line">
                <tr>
                  <th className="px-4 py-3 text-left">Item</th>
                  <th className="px-3 py-3 text-left">SKU</th>
                  <th className="px-3 py-3 text-left">Category</th>
                  <th className="px-3 py-3 text-left">Warehouse</th>
                  <th className="px-3 py-3 text-right">Qty</th>
                  <th className="px-3 py-3 text-left">Unit</th>
                  <th className="px-3 py-3 text-right">Value</th>
                  <th className="px-3 py-3 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <StockRow key={row.balanceId} row={row} />
                ))}
              </tbody>
            </table>
          </div>

          {/* Footer stats */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-line bg-raised px-4 py-2.5 text-xs text-muted">
            <span className="inline-flex items-center gap-1.5">
              Showing{" "}
              <strong className="font-mono tabular-nums font-bold text-ink">{rows.length} rows</strong>
            </span>
            <span aria-hidden className="h-3 w-px bg-line-2" />
            <span className="inline-flex items-center gap-1.5">
              Total value{" "}
              <strong className="font-mono tabular-nums font-bold text-ink">
                {paiseToInr(totalValue.toString())}
              </strong>
            </span>
            <span aria-hidden className="h-3 w-px bg-line-2" />
            <span className="inline-flex items-center gap-1.5">
              Low stock{" "}
              <strong className="font-mono tabular-nums font-bold text-ink">{lowStockCount}</strong>
            </span>
          </div>

          {/* Pagination */}
          <div className="border-t border-line">
            <ListPagination
              pageNumber={pageNumber}
              showingFrom={showingFrom}
              showingTo={showingTo}
              sortLabel="sorted by item"
              hasPrev={cursorStack.length > 0}
              hasNext={!!summary.data?.nextCursor}
              onPrev={handlePrev}
              onNext={handleNext}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Quick filter pill ─────────────────────────────────────────────

function QuickFilterPill({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "inline-flex items-center h-7 px-3 rounded-full text-xs font-medium border transition-colors",
        active
          ? "bg-ink text-paper border-ink"
          : "bg-paper text-ink-2 border-line hover:border-line-2 hover:bg-bg",
      ].join(" ")}
    >
      {label}
    </button>
  );
}

// ── Category pill ─────────────────────────────────────────────────

const CATEGORY_PALETTE: string[] = [
  "bg-sky-50 text-sky-700 border-sky-100",
  "bg-indigo-50 text-indigo-700 border-indigo-100",
  "bg-violet-50 text-violet-700 border-violet-100",
  "bg-emerald-50 text-emerald-700 border-emerald-100",
  "bg-amber-50 text-amber-700 border-amber-100",
  "bg-rose-50 text-rose-700 border-rose-100",
  "bg-orange-50 text-orange-700 border-orange-100",
  "bg-teal-50 text-teal-700 border-teal-100",
];

function categoryPill(category: string): string {
  let hash = 0;
  for (let i = 0; i < category.length; i++) {
    hash = (hash * 31 + category.charCodeAt(i)) & 0xff_ff_ff;
  }
  return CATEGORY_PALETTE[hash % CATEGORY_PALETTE.length] ?? CATEGORY_PALETTE[0]!;
}

// ── KPI tile — mirrors invoice list Tile ──────────────────────────

type Accent = "sky" | "emerald" | "amber" | "rose" | "muted";

const ACCENT_BAR: Record<Accent, string> = {
  sky: "bg-sky-500",
  emerald: "bg-emerald-500",
  amber: "bg-amber-400",
  rose: "bg-rose-500",
  muted: "bg-line",
};
const ACCENT_ICON: Record<Accent, string> = {
  sky: "bg-sky-50 text-sky-600",
  emerald: "bg-emerald-50 text-emerald-600",
  amber: "bg-amber-50 text-amber-600",
  rose: "bg-rose-50 text-rose-600",
  muted: "bg-bg text-muted",
};

function KpiTile({
  accent,
  icon,
  label,
  value,
  meta,
}: {
  accent: Accent;
  icon: React.ReactNode;
  label: string;
  value: string;
  meta: string;
}) {
  return (
    <Card className="relative min-w-0 overflow-hidden transition-all hover:-translate-y-px hover:border-line-2 hover:shadow-md">
      <span aria-hidden className={`absolute inset-y-0 left-0 w-1 ${ACCENT_BAR[accent]}`} />
      <CardBody className="px-4 py-3.5">
        <div className="min-w-0 flex-1">
          <div className="mb-1.5 flex items-center justify-between gap-2 text-xs text-muted">
            <p className="min-w-0 truncate">{label}</p>
            <span
              aria-hidden
              className={`grid size-7 shrink-0 place-items-center rounded-md ${ACCENT_ICON[accent]}`}
            >
              {icon}
            </span>
          </div>
          <p className="text-xl font-bold leading-tight text-ink truncate">{value}</p>
          <p className="mt-1 text-xs text-muted truncate">{meta}</p>
        </div>
      </CardBody>
    </Card>
  );
}

// ── Table row ─────────────────────────────────────────────────────

function StockRow({ row }: { row: SummaryRow }) {
  const isOut = row.quantityOnHand === 0;
  const isLow = row.isBelowReorder && !isOut;

  return (
    <tr className="border-t border-line hover:bg-raised transition-colors">
      {/* Item — avatar + name + WAC + reorder meta */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <span
            aria-hidden
            className={`grid h-7 w-7 shrink-0 place-items-center rounded-md text-xs font-bold text-white ${avatarClassFor(row.itemName)}`}
          >
            {initialsFor(row.itemName)}
          </span>
          <div className="min-w-0">
            <span className="block truncate font-semibold text-ink text-sm leading-tight">
              {row.itemName}
            </span>
            <span className="flex items-center gap-1 mt-0.5 text-xs text-muted font-mono truncate">
              <span>WAC {paiseToInrShort(row.valuationRatePaise)}/{row.itemUnit}</span>
              {row.reorderLevel != null && (
                <>
                  <span aria-hidden className="text-line-2">·</span>
                  <span className={row.isBelowReorder ? "text-amber-600 font-medium font-sans" : ""}>
                    reorder at {row.reorderLevel.toLocaleString("en-IN")}
                  </span>
                </>
              )}
            </span>
          </div>
        </div>
      </td>
      {/* SKU */}
      <td className="px-3 py-3">
        <span className="font-mono text-xs text-muted truncate block">{row.itemSku ?? "—"}</span>
      </td>
      {/* Category */}
      <td className="px-3 py-3">
        {row.itemCategory ? (
          <span className={`inline-flex items-center px-2 py-0.5 rounded-lg text-xs font-medium border truncate max-w-[110px] ${categoryPill(row.itemCategory)}`}>
            {row.itemCategory}
          </span>
        ) : (
          <span className="text-xs text-muted">—</span>
        )}
      </td>
      {/* Warehouse */}
      <td className="px-3 py-3">
        <span className="text-xs text-ink-2 truncate block">{row.warehouseName}</span>
      </td>
      {/* Qty */}
      <td className="px-3 py-3 text-right">
        <span className="font-mono font-semibold text-sm text-ink tabular-nums">
          {row.quantityOnHand.toLocaleString("en-IN")}
        </span>
      </td>
      {/* Unit */}
      <td className="px-3 py-3">
        <span className="text-xs text-muted">{row.itemUnit}</span>
      </td>
      {/* Value */}
      <td className="px-3 py-3 text-right">
        <span className="font-mono font-semibold text-sm text-ink tabular-nums">
          {paiseToInrShort(row.totalValuePaise)}
        </span>
      </td>
      {/* Status — dot + label pill, mirrors invoice list */}
      <td className="px-3 py-3">
        {isOut ? (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-rose-50 text-rose-600 border border-rose-100">
            <span aria-hidden className="w-1.5 h-1.5 rounded-full bg-rose-500 shrink-0" />
            Out of stock
          </span>
        ) : isLow ? (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-600 border border-amber-100">
            <span aria-hidden className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />
            Low stock
          </span>
        ) : null}
      </td>
    </tr>
  );
}
